/**
 * Guided Diagonal — one-screen prism-lead wizard.
 *
 * Flow (matches the surveyor's field workflow):
 *   1. HOME
 *      · Laser is ON. AimPad on screen. Surveyor manually drives
 *        the scope onto the "home" location (e.g. first corner of
 *        the shape) and taps SET HOME. That fires a PowerSearch to
 *        acquire the prism sitting at that home mark so the rest
 *        of the run can auto-track.
 *   2. CORNERS (A → B → C → D)
 *      · The surveyor walks the prism to the corner. The tablet
 *        polls scope-state at 5 Hz. When the prism goes still for
 *        ~600 ms we auto-record the shot with a loud confirmation
 *        beep + toast, then advance to the next corner.
 *      · If the prism lock drops we continuously call `reacquire`
 *        (which stares at the last-known position). A big
 *        SEARCH AT HOME fallback button re-runs the wide PowerSearch
 *        from the saved home location if the prism can't be
 *        picked up near where it was last seen.
 *   3. COMPLETE — auto-navigate back to the classic session view
 *      so the diagonals, side lengths, elevation chips and PDF
 *      export are immediately usable.
 *
 * The classic step-by-step view still works via the "Skip guided"
 * button. Everything below reuses the same /api/diagonal/... routes
 * as the classic view so a partially-completed guided run resumes
 * cleanly in either flow.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Crosshair, Home as HomeIcon, Search as SearchIcon,
  Volume2, VolumeX, Zap,
} from "lucide-react";
import { station } from "../lib/api";
import { AimPad } from "../components/AimPad";

const CORNER_NAMES = ["A", "B", "C", "D"];
// Minimum angular displacement (radians) the prism must move from
// the last recorded shot before `armed` will trip and allow the
// next auto-shot. 20 mrad ≈ 20 cm @ 10 m — comfortably below the
// smallest sensible flange diagonal (typically metres between
// corners) but well above any hand-held prism jitter or ATR
// settling flap. This is the anti-phantom-shot gate that survives
// mid-walk lock loss + re-lock: even if the still-detector says
// "isStill" during a brief pause between corners, the auto-shot
// still won't fire until the prism is decidedly at a new position.
const NEW_CORNER_RAD = 0.020;

// Post-relock re-arm distance. After the ATR loses lock and re-
// acquires (mid-walk lock loss + Stage 1 stare, or full Stage 2
// return-to-home PowerSearch), we don't trust the "new corner"
// 20 mrad gate — the surveyor may have to walk a metre or more from
// where the prism was re-locked before they reach the actual next
// corner. 100 mrad ≈ 1 m @ 10 m range is a comfortable safety
// margin: comfortably above ATR settling flap, well within a
// realistic walking distance to the next corner. This is the
// anti-"phantom shot after relock" gate the surveyor asked for
// (Feb 2026): "once locked it will prompt you to go to next point"
// — the auto-shot stays disabled until the prism has genuinely
// walked the required distance away from the re-lock position.
const RELOCK_REARM_RAD = 0.100;

// Sub-mrad angular still threshold — matches SetoutGuided.
const STILL_RAD = 0.00035;
const STILL_MS = 600;

// ------- Audio helpers -----------------------------------------------------
// Small standalone tone player. Two use-cases:
//   * `beep()`  — short "point recorded" chirp after every shot
//   * `blip()`  — single tick during search / countdown
// Kept minimal so it doesn't compete with the setout beeper.
function useTones(muted) {
  const ctxRef = useRef(null);
  const ensureCtx = useCallback(() => {
    if (muted) return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    return ctxRef.current;
  }, [muted]);

  const tone = useCallback((freq, durMs, gain = 0.25) => {
    const c = ensureCtx();
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sine"; osc.frequency.value = freq;
    osc.connect(g); g.connect(c.destination);
    const now = c.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durMs / 1000);
    osc.start(now);
    osc.stop(now + durMs / 1000 + 0.02);
  }, [ensureCtx]);

  const chime = useCallback(() => {
    // Two-tone confirmation — musical, unmistakable "captured".
    tone(880, 90);
    setTimeout(() => tone(1320, 140), 90);
  }, [tone]);

  const lockChime = useCallback(() => {
    // Rising interval — different from `chime` so the surveyor can
    // tell "prism locked" apart from "point captured" by ear alone.
    tone(660, 80);
    setTimeout(() => tone(880, 80), 80);
    setTimeout(() => tone(1100, 120), 160);
  }, [tone]);

  const blip = useCallback(() => tone(660, 60, 0.15), [tone]);
  const buzz = useCallback(() => tone(220, 200, 0.2), [tone]);

  // Speech synthesis — hands-free confirmation. Preferred voice
  // biases toward a clear English one so "Prism Locked" reads
  // clearly even in a noisy site. Silent when muted or when the
  // browser has no SpeechSynthesis (older webviews).
  const voiceRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      voiceRef.current =
        voices.find((v) => /en-US|en_US/.test(v.lang) && /Google|Samantha|Karen|Daniel/i.test(v.name)) ||
        voices.find((v) => /^en/i.test(v.lang)) ||
        voices[0];
    };
    pick();
    window.speechSynthesis.onvoiceschanged = pick;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);
  const speak = useCallback((text) => {
    if (muted) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      // Cancel any in-flight utterance so back-to-back announcements
      // (e.g. lock lost → lock re-acquired) don't queue up.
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      if (voiceRef.current) u.voice = voiceRef.current;
      u.rate = 1.05;
      u.pitch = 1.0;
      u.volume = 1.0;
      window.speechSynthesis.speak(u);
    } catch { /* silent */ }
  }, [muted]);

  useEffect(() => () => {
    try {
      const c = ctxRef.current;
      if (c && c.state !== "closed") c.close();
    } catch { /* silent */ }
    ctxRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
  }, []);

  return { chime, lockChime, blip, buzz, speak };
}

// Still-detector (identical logic to SetoutGuided).
function useStillDetector(thresholdRad = STILL_RAD, dwellMs = STILL_MS) {
  const historyRef = useRef([]);
  const stillSinceRef = useRef(null);
  const [isStill, setStill] = useState(false);

  const observe = useCallback((hz, v) => {
    if (!Number.isFinite(hz) || !Number.isFinite(v)) return;
    const H = historyRef.current;
    H.push({ hz, v, t: Date.now() });
    if (H.length > 8) H.shift();
    const hzs = H.map((p) => p.hz), vs = H.map((p) => p.v);
    const hRange = Math.max(...hzs) - Math.min(...hzs);
    const vRange = Math.max(...vs) - Math.min(...vs);
    const still = H.length >= 4 && hRange < thresholdRad && vRange < thresholdRad;
    if (still) {
      if (stillSinceRef.current == null) stillSinceRef.current = Date.now();
      setStill(Date.now() - stillSinceRef.current >= dwellMs);
    } else {
      stillSinceRef.current = null;
      setStill(false);
    }
  }, [thresholdRad, dwellMs]);

  const reset = useCallback(() => {
    historyRef.current = [];
    stillSinceRef.current = null;
    setStill(false);
  }, []);

  return { isStill, observe, reset };
}

// ------- API surface (matches DiagonalCheck) -------------------------------
const buildApi = (api) => ({
  addShot: (id, b) => api.post(`/diagonal/sessions/${id}/shots`, b).then((r) => r.data),
  delShot: (id, sid) => api.delete(`/diagonal/sessions/${id}/shots/${sid}`).then((r) => r.data),
});

// ------- Big status pane ---------------------------------------------------
const StageBanner = ({ tone, title, subtitle, cta }) => (
  <div
    className={`rounded-md border p-4 text-center transition-all ${
      tone === "search" ? "border-[#F5A623]/60 bg-[#F5A623]/10 text-[#F5A623]"
        : tone === "captured" ? "border-[#8DC63F]/60 bg-[#8DC63F]/10 text-[#8DC63F]"
        : tone === "lost" ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-[#FF3B30]"
        : "border-[#009E8E]/40 bg-[#009E8E]/10 text-[#00E1C7]"
    }`}
    style={{ fontFamily: "Chakra Petch, sans-serif" }}
  >
    <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">{title}</div>
    <div className="mt-1 text-3xl font-black leading-tight">{subtitle}</div>
    {cta && <div className="mt-2 text-[11px] uppercase tracking-widest opacity-80">{cta}</div>}
  </div>
);

// ---------------------------------------------------------------------------

export default function DiagonalGuided({ current, api, setCurrent, onExit }) {
  const diagApi = useMemo(() => buildApi(api), [api]);
  const [scope, setScope] = useState({});
  const [muted, setMuted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [homeSearching, setHomeSearching] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const { chime, lockChime, blip, buzz, speak } = useTones(muted);
  const { isStill, observe, reset: resetStill } = useStillDetector();

  // ---- Derived phase --------------------------------------------------
  //   home    → surveyor still setting home / search hasn't succeeded
  //   corner  → 0..3 next slot to shoot
  //   complete
  const shotIndices = useMemo(
    () => new Set((current?.shots || []).map((s) => s.index)),
    [current]
  );
  const nextIdx = useMemo(() => {
    for (let i = 0; i < 4; i++) if (!shotIndices.has(i)) return i;
    return 4;
  }, [shotIndices]);

  const [homeSet, setHomeSet] = useState(false);
  // Once the surveyor taps SET HOME + search succeeds we advance to
  // corner shooting. Persist across page loads is out of scope — a
  // resumed session simply starts at the current nextIdx.
  const phase = homeSet ? (nextIdx >= 4 ? "complete" : "corner") : "home";

  // ---- Warm-up grace period -------------------------------------------
  // The Leica ATR firmware needs a few seconds to settle after any
  // fresh acquisition — during that window it flaps LOCK/LOST multiple
  // times, sometimes for >500 ms on each side. If we accept those flap
  // frames as real, the wizard: (1) buzzes "prism lost" seconds after
  // the surveyor hit SET HOME, (2) fires the re-lock reset when the
  // flap re-locks, and (3) auto-records the corner because armed/still
  // reads true on the stationary prism. `warming` suppresses every
  // sensitive path for the first 3 s after entering corner phase,
  // and again for 1.5 s after any successful shot (settling before
  // the next corner). Timer resets whenever phase changes, whenever
  // the corner index changes, and after each shot.
  // Separate warm-up sources so a re-lock event can extend the settle
  // window without wiping the corner-entry timer (and vice versa).
  // Both feed the single `warming` gate the auto-shot / hasMoved logic
  // reads. Re-lock warming is critical: without it the ATR settling
  // jitter on the just-reacquired prism fakes an isStill true→false→
  // true sequence, which latches `hasMoved` and fires an auto-record
  // ON the current position — exactly the "phantom re-lock shot" bug.
  const [entryWarming, setEntryWarming] = useState(true);
  const [relockUntil, setRelockUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Tick a 200 ms clock only while a re-lock warm-up is armed so
    // downstream effects see `warming` flip false at the right moment
    // without a permanent 5 Hz React render loop.
    if (relockUntil <= now) return;
    const iv = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(iv);
  }, [relockUntil, now]);
  const warming = entryWarming || now < relockUntil;
  const shotsSeenRef = useRef(0);
  useEffect(() => {
    if (phase !== "corner") { setEntryWarming(true); return; }
    // Longer settle on entry to corner (fresh from PowerSearch) than
    // between corners (prism only just re-armed after a shot).
    const isEntry = shotsSeenRef.current === 0;
    setEntryWarming(true);
    const t = setTimeout(() => setEntryWarming(false), isEntry ? 3000 : 1500);
    return () => clearTimeout(t);
  }, [phase, nextIdx]);

  // ---- Debounced lock state --------------------------------------------
  // The Leica ATR flaps LOCK/LOST a handful of times when it first
  // grabs the prism (and briefly at any subsequent re-lock). Reacting
  // to every raw transition would (a) buzz the surveyor with false
  // "prism lost" toasts during initial acquisition and (b) fire the
  // re-lock reset before the auto-shot gate got a chance to arm,
  // meaning the shot still fires on a bad lock.
  //
  // `stableLocked` follows `scope.atr_locked` but only after the raw
  // flag has held the same value for STABLE_POLLS consecutive polls
  // (~500 ms at the 10 Hz corner-phase poll rate). All downstream
  // logic — buzz, recovery loop, re-lock reset — reads THIS not the
  // raw flag.
  const STABLE_POLLS = 5;
  const [stableLocked, setStableLocked] = useState(true);
  const rawLockRef = useRef(null);
  const lockStreakRef = useRef(0);
  // Ref (not state) so downstream effects reading it see the update
  // WITHIN the same render as stableLocked flipping true — fixes the
  // one-render race where the auto-shot effect (declared before the
  // re-lock reset effect) fired with stale hasMoved=true BEFORE the
  // reset had a chance to schedule relockUntil.
  const stableLockedSinceRef = useRef(0);
  useEffect(() => {
    const raw = !!scope.atr_locked;
    if (raw === rawLockRef.current) {
      lockStreakRef.current += 1;
    } else {
      rawLockRef.current = raw;
      lockStreakRef.current = 1;
    }
    if (lockStreakRef.current >= STABLE_POLLS && stableLocked !== raw) {
      if (raw) stableLockedSinceRef.current = Date.now();
      setStableLocked(raw);
    }
  }, [scope.atr_locked, stableLocked]);

  // Turn the laser on the moment we enter guided mode AND keep it on
  // during the HOME phase — while the surveyor is still aiming at
  // their home mark. CRITICAL: once the prism is locked (or a search
  // is in progress), we MUST NOT re-fire `station.laser(true)`. That
  // RPC path disables ATR, disables USER_LOCK, clears the RTRK_DIST
  // tracking measurement, drops MOT out of LOCK mode, and switches
  // the target type to reflectorless — every single step kills the
  // lock we just worked so hard to establish. That was the "Set Home
  // & Lock Prism engages then instantly loses lock" bug: powerSearch
  // completes, leaves laser_on=false (because it's using the EDM for
  // tracking), the poll observes laser_on=false, this effect fires
  // laser(true), and the lock is destroyed inside 200 ms.
  //
  // Rules:
  //  - Only kick the laser while lock is NOT engaged and not
  //    searching. Before initial home lock, this keeps the aim spot
  //    visible on the target.
  //  - Once atr_locked is TRUE (or atr_searching is TRUE), do
  //    nothing — the surveyor doesn't need the visible laser any
  //    more, the servo is following the prism.
  const laserKickRef = useRef(0);
  useEffect(() => {
    if (phase !== "home") return;
    // Lock or search in progress → don't touch the EDM.
    if (scope.atr_locked || scope.atr_searching || homeSearching) return;
    const now = Date.now();
    if (scope.laser_on) { laserKickRef.current = now; return; }
    if (now - laserKickRef.current < 1500) return;
    laserKickRef.current = now;
    station.laser(true).catch((e) => {
      console.warn("laser(true) failed", e);
    });
  }, [phase, scope.laser_on, scope.atr_locked, scope.atr_searching, homeSearching]);

  // Live scope poll. 200 ms during corner phase (same as home/complete)
  // — the earlier 100 ms poll doubled the state-endpoint traffic and
  // amplified the bus load already caused by the bridge's own lock
  // poll, which measurably hurt tracking. Combined with the bridge's
  // 2 Hz lock health poll + tightened hysteresis, 5 Hz on the tablet
  // still surfaces a lost target within ~2 s of the bridge flagging it.
  useEffect(() => {
    let cancel = false;
    const iv = setInterval(async () => {
      try {
        const s = await station.state();
        if (cancel) return;
        setScope(s || {});
        observe(s?.hz, s?.v);
      } catch { /* silent */ }
    }, 200);
    return () => { cancel = true; clearInterval(iv); };
  }, [observe]);

  // ---- Auto-shot on stop (corners only) -------------------------------
  // Three gates protect against a false shot:
  //   1. `armed` — must see the prism angles change since the last shot
  //      (guards against a frozen sim / disconnected bridge)
  //   2. `hasMoved` — must have observed at least one poll where the
  //      still-detector reported NOT-still since entering this corner.
  //      Without this, the moment we transition from home→corner (where
  //      the prism is already sitting still on the home mark) the still
  //      detector fires immediately and we'd auto-record home as the
  //      corner. We only unlock the shot after the surveyor physically
  //      picks up the pole and starts walking.
  //   3. `captured` — dedupe within a single still window.
  const [captured, setCaptured] = useState(false);
  const [armed, setArmed] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const lastPollRef = useRef({ hz: null, v: null });
  // Hz/V (radians) of the last recorded shot on this session. The
  // armed detector below refuses to trip until the current prism
  // position is at least NEW_CORNER_RAD away from this reference —
  // the anti-phantom-shot gate that survives mid-walk re-locks and
  // brief stops between corners. Populated from `current.shots`
  // (last entry) whenever we render, so opening a partly-filled
  // session picks up the last-shot reference correctly.
  const lastShotAngleRef = useRef(null);
  // Hz/V (radians) captured at the exact moment a stable false→true
  // relock transition happens during corner phase. While this ref is
  // set, the armed gate below uses RELOCK_REARM_RAD (100 mrad) from
  // THIS position instead of NEW_CORNER_RAD (20 mrad) from the last
  // shot — the "prism must have genuinely walked to the next corner"
  // safety margin. Cleared after a successful shot (next corner
  // capture) or when arming trips (surveyor has moved far enough).
  const relockAnchorRef = useRef(null);
  useEffect(() => {
    const last = (current?.shots || []).slice(-1)[0];
    if (last && Number.isFinite(last.hz) && Number.isFinite(last.v)) {
      lastShotAngleRef.current = { hz: last.hz, v: last.v };
    } else {
      lastShotAngleRef.current = null;
    }
  }, [current?.shots?.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fresh corner (or phase change) = re-arm from scratch.
    setCaptured(false);
    setArmed(false);
    setHasMoved(false);
    lastPollRef.current = { hz: null, v: null };
    relockAnchorRef.current = null;
    resetStill();
  }, [nextIdx, phase, resetStill]);

  // Arm when the prism has moved a corner-scale distance from the
  // LAST RECORDED SHOT (not just any movement). This is the anti-
  // phantom-shot gate that survives mid-walk lock loss + re-lock
  // followed by a brief pause: even if the still-detector says
  // "isStill" for a beat between corners, `armed` stays false
  // because the prism is still within NEW_CORNER_RAD of the last
  // shot. Once the surveyor gets the prism to a genuinely new
  // corner, armed trips and the shot fires on stillness.
  //
  // If there's no previous shot (first corner of the run), fall
  // back to the old "any movement > STILL_RAD" logic so the
  // initial pick-up from the tripod arms us for corner A.
  useEffect(() => {
    if (phase !== "corner" || armed || !stableLocked || warming) return;
    const hz = scope.hz, v = scope.v;
    if (!(Number.isFinite(hz) && Number.isFinite(v))) return;
    // Post-relock takes precedence over the last-shot gate. See
    // `relockAnchorRef` docstring — after a mid-walk lock loss +
    // re-lock, we require a much larger 100 mrad displacement from
    // the re-lock position before arming, so the auto-shot never
    // fires on the surveyor's way to the actual next corner.
    const anchor = relockAnchorRef.current;
    if (anchor) {
      const dhz = Math.abs(hz - anchor.hz);
      const dv = Math.abs(v - anchor.v);
      if (dhz > RELOCK_REARM_RAD || dv > RELOCK_REARM_RAD) {
        relockAnchorRef.current = null;   // consumed — back to normal gate
        setArmed(true);
      }
      return;
    }
    const lastShot = lastShotAngleRef.current;
    if (lastShot) {
      const dhz = Math.abs(hz - lastShot.hz);
      const dv = Math.abs(v - lastShot.v);
      if (dhz > NEW_CORNER_RAD || dv > NEW_CORNER_RAD) setArmed(true);
    } else {
      const prev = lastPollRef.current;
      if (prev.hz != null) {
        const dhz = Math.abs(hz - prev.hz);
        const dv = Math.abs(v - prev.v);
        if (dhz > STILL_RAD || dv > STILL_RAD) setArmed(true);
      }
      lastPollRef.current = { hz, v };
    }
  }, [scope.hz, scope.v, armed, phase, stableLocked, warming]);

  // Latch `hasMoved` the first time we see the still-detector go from
  // still→moving during this corner phase. Initial `isStill=false` (an
  // empty buffer right after resetStill) doesn't count as movement —
  // only an actual transition out of stillness does. Additionally, we
  // ignore the transition until the lock is STABLY locked so that the
  // servo jitter during initial ATR acquisition doesn't fake movement.
  const prevStillRef = useRef(false);
  useEffect(() => {
    if (phase !== "corner") { prevStillRef.current = false; return; }
    if (!stableLocked || warming) { prevStillRef.current = false; return; }
    // Same 3-s post-re-lock guard the auto-shot uses — ATR settling
    // jitter must not fake a still→moving transition that would arm
    // hasMoved on the just-reacquired prism.
    if (Date.now() - stableLockedSinceRef.current < 3000) {
      prevStillRef.current = false;
      return;
    }
    if (prevStillRef.current && !isStill && !hasMoved) {
      setHasMoved(true);
    }
    prevStillRef.current = isStill;
  }, [isStill, phase, hasMoved, stableLocked, warming]);

  // Fire the shot when armed + moved + still + stably locked + warmed up.
  useEffect(() => {
    if (phase !== "corner") return;
    if (warming) return;                         // suppress during ATR settle
    if (!stableLocked) return;                   // block during flap windows
    // Synchronous re-lock guard — the state-based `warming` gate has a
    // one-render race with the auto-shot effect (declared before the
    // re-lock reset effect). This ref is written inside the debounce
    // useEffect the moment stableLocked flips true, so by the time
    // this body runs we always see the fresh timestamp. 3 s covers
    // the ATR settle window on the just-reacquired prism.
    if (Date.now() - stableLockedSinceRef.current < 3000) return;
    // `armed` alone is enough to prove the surveyor moved the prism —
    // requiring `hasMoved` additionally meant a full still→moving→still
    // sequence, which failed to latch when the surveyor picked up the
    // prism during entry-warming (very common — the moment they see
    // "prism locked" they walk). The auto-shot then never fired
    // because `hasMoved` was stuck false. Sanity anti-duplicate is
    // still enforced further down via the "5 mm same-position" check.
    if (!armed || !isStill || captured || busy) return;
    if (!Number.isFinite(scope.hz)) return;
    (async () => {
      setBusy(true); setCaptured(true);
      try {
        const m = await station.measure();
        if (!Number.isFinite(m?.x) || !Number.isFinite(m?.y) || !Number.isFinite(m?.z)) {
          throw new Error("no coord");
        }
        // Sanity gate — if the ATR silently released lock but the
        // bridge's keep-alive grace is still reporting `atr_locked=true`,
        // the servo head is frozen at the last-known position. Every
        // "auto-shot" then records identical coordinates, filling
        // A-D with the same point. Reject the shot when the measured
        // coordinates are within 5 mm (in any axis) of the previous
        // corner — real walking motion between corners is always
        // decimetres to metres.
        const prev = (current.shots || []).slice(-1)[0];
        if (prev &&
            Math.abs(prev.x - m.x) < 0.005 &&
            Math.abs(prev.y - m.y) < 0.005 &&
            Math.abs(prev.z - m.z) < 0.005) {
          buzz();
          speak("Same position. Move the prism.");
          toast.error("Same position as last corner — prism didn't move");
          setCaptured(false);
          setHasMoved(false);
          return;
        }
        const s = await diagApi.addShot(current.id, {
          index: nextIdx,
          label: CORNER_NAMES[nextIdx],
          x: m.x, y: m.y, z: m.z,
          hz: m.hz, v: m.v, sd: m.sd,
        });
        setCurrent(s);
        shotsSeenRef.current += 1;
        chime();
        // If this shot completes the run, celebrate; otherwise nudge
        // the surveyor toward the next corner by voice.
        const nextName = CORNER_NAMES[nextIdx + 1];
        if (nextIdx >= 3) {
          speak("All four corners recorded. Tap view results, or tap a corner to re-shoot.");
        } else {
          speak(`Corner ${CORNER_NAMES[nextIdx]} recorded. Walk to corner ${nextName}`);
        }
        toast.success(`Corner ${CORNER_NAMES[nextIdx]} recorded`);
      } catch (e) {
        buzz();
        toast.error(e?.message === "no coord"
          ? "Measurement failed — aim at target"
          : "Auto-shot failed");
        setCaptured(false);
      } finally {
        setBusy(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStill, phase, nextIdx]);

  // ---- Lost-lock recovery ---------------------------------------------
  // Only kick reacquire once the lock is STABLY lost AND we're past the
  // ATR-settle warm-up — one-frame flaps during initial acquisition
  // never make it here.
  const lockLost = phase === "corner" && homeSet && !warming && stableLocked === false;

  // Buzz + red toast on the exact stable true→false transition.
  const lastLockAlertRef = useRef(true);
  useEffect(() => {
    if (phase !== "corner" || warming) {
      lastLockAlertRef.current = true; return;
    }
    if (lastLockAlertRef.current && !stableLocked) {
      buzz();
      speak("Prism lost");
      toast.error(`Prism lost — searching`);
    }
    lastLockAlertRef.current = stableLocked;
  }, [stableLocked, phase, buzz, warming, speak]);

  // Watch stable lock state to suppress a phantom auto-shot the
  // instant the prism is re-acquired. On a stable false→true we reset
  // hasMoved, armed, captured so the surveyor has to physically move
  // again before another shot can trigger. Suppressed during warm-up
  // so a flap during initial acquisition doesn't trip this and toast
  // the surveyor with "Re-locked" seconds after SET HOME.
  const prevLockedRef = useRef(true);
  const phasePrevRef = useRef(phase);
  useEffect(() => {
    if (phase !== "corner") {
      prevLockedRef.current = true;
      phasePrevRef.current = phase;
      return;
    }
    // First observation after entering corner phase — seed the baseline
    // without firing the re-lock toast.
    if (phasePrevRef.current !== "corner") {
      prevLockedRef.current = stableLocked;
      phasePrevRef.current = phase;
      return;
    }
    if (warming) { prevLockedRef.current = stableLocked; return; }
    if (!prevLockedRef.current && stableLocked) {
      // Stable false → true re-lock during corner phase (past warm-up).
      setHasMoved(false);
      setArmed(false);
      setCaptured(false);
      resetStill();
      lastPollRef.current = { hz: null, v: null };
      prevStillRef.current = false;
      // Anchor the re-lock position. Auto-shot now requires the
      // prism to walk RELOCK_REARM_RAD (100 mrad) away from HERE
      // before it can arm — protects against the "phantom shot on
      // the way to the next corner" bug the surveyor hit repeatedly.
      if (Number.isFinite(scope.hz) && Number.isFinite(scope.v)) {
        relockAnchorRef.current = { hz: scope.hz, v: scope.v };
      }
      // Arm a 3-second re-lock warm-up so the ATR settling jitter on
      // the just-reacquired prism can't fake a still→moving→still
      // sequence that latches `hasMoved` and fires a phantom shot at
      // the current position. Same mechanism as corner-entry warming;
      // downstream gates (auto-shot, hasMoved latch) already read the
      // combined `warming` flag. After warming, normal armed
      // detection (any movement > STILL_RAD) resumes — the surveyor
      // moves the prism naturally on the way to the next corner
      // (or picks it up + puts it down at the current corner if the
      // re-lock happened right at the target), and the standard
      // still→moving→still chain fires the shot.
      setRelockUntil(Date.now() + 3000);
      setNow(Date.now());
      lockChime();
      speak(`Prism re-locked. Walk to corner ${CORNER_NAMES[nextIdx]}`);
      toast.success(`Prism re-locked — walk to ${CORNER_NAMES[nextIdx]}`);
    }
    prevLockedRef.current = stableLocked;
    // scope.hz/v deliberately excluded — we only sample them at the
    // moment of a stable false→true transition, not on every angle
    // update. Adding them would re-run this effect 4× per second.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableLocked, phase, nextIdx, resetStill, warming, lockChime, speak]);

  useEffect(() => {
    if (!lockLost || recovering || busy) return;
    let cancel = false;
    (async () => {
      setRecovering(true);
      // Loop reacquire until locked or cancelled. No grace-pause — the
      // total station has already reported "target lost" so we start
      // hunting immediately.
      while (!cancel) {
        try {
          const s = await station.reacquire();
          if (s?.atr_locked) {
            toast.success("Prism re-locked");
            break;
          }
        } catch { /* silent */ }
        blip();
        // Longer cooldown between reacquire attempts. Each reacquire
        // can take up to 8-10 s (Stage 1 slew-to-last-known-aim +
        // atr_search retry loop, then Stage 2 return-to-hunt-home +
        // power_search) during which the bridge's `_bus_lock` is
        // held and background angle polling can't update — the tablet
        // Hz/V readout freezes. Waiting 3 s between attempts gives
        // `_refresh` room to breathe so the UI stays live while
        // recovery continues in the background. Was 1.2 s.
        await new Promise((r) => setTimeout(r, 3000));
      }
      setRecovering(false);
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockLost]);

  // Announce first stable lock after entering corner phase. Separate
  // from the re-lock effect above — that one only fires on false→true
  // TRANSITIONS *after* the phase baseline was seeded, so it never
  // catches the very first "yes, we're locked" event for a session.
  const firstLockRef = useRef(false);
  useEffect(() => {
    if (phase !== "corner") { firstLockRef.current = false; return; }
    if (warming) return;
    if (!stableLocked || firstLockRef.current) return;
    firstLockRef.current = true;
    lockChime();
    speak(`Prism locked. Walk to corner ${CORNER_NAMES[nextIdx]}`);
    toast.success(`Prism locked — walk to ${CORNER_NAMES[nextIdx]}`);
  }, [phase, warming, stableLocked, nextIdx, lockChime, speak]);
  const setHomeAndSearch = async () => {
    setHomeSearching(true);
    try {
      // Save current aim as home (session-scoped, persists in bridge).
      const s = await station.state();
      if (Number.isFinite(s?.hz) && Number.isFinite(s?.v)) {
        await station.homeSet({
          tool: "diagonal",
          session_id: current.id,
          hz: s.hz, v: s.v,
        }).catch(() => undefined);
      }
      // Set the bridge's Start Point (used by reacquire Stage 2).
      await station.setStartPoint().catch(() => undefined);
      // Now search for a prism at the home location.
      const after = await station.powerSearch();
      if (after?.atr_locked) {
        toast.success("Prism locked at home — walk to corner A");
        setHomeSet(true);
      } else {
        toast.error(after?.last_error || "No prism found at home — retry");
      }
    } catch (e) {
      toast.error(e?.message || "Search failed");
    } finally {
      setHomeSearching(false);
    }
  };

  const searchAtHome = async () => {
    // Explicit "give up on the last-known location, go back to home
    // and PowerSearch there". Cancels any current reacquire loop
    // implicitly because `station.atr({action:"stop"})` releases the
    // servo, then re-runs the acquisition sequence.
    //
    // Arm the re-lock warm-up SYNCHRONOUSLY here, before the RPC
    // fires. This is critical: the useEffect that resets hasMoved /
    // armed / captured on stableLocked false→true has a one-render
    // race against the auto-shot useEffect — the auto-shot could
    // fire on the same render the reset schedules. By setting
    // relockUntil right now (well before stableLocked can flip
    // true), the `warming` gate is already TRUE by the time the
    // lock stabilises, blocking the phantom shot at home.
    setHomeSearching(true);
    setHasMoved(false);
    setArmed(false);
    setCaptured(false);
    resetStill();
    lastPollRef.current = { hz: null, v: null };
    prevStillRef.current = false;
    // Anchor the "at-home" relock position so the 100 mrad re-arm
    // gate kicks in as soon as the PowerSearch succeeds — surveyor
    // must walk properly away before the next auto-shot can fire.
    try {
      const s = await station.state();
      if (Number.isFinite(s?.hz) && Number.isFinite(s?.v)) {
        relockAnchorRef.current = { hz: s.hz, v: s.v };
      }
    } catch { /* silent */ }
    setRelockUntil(Date.now() + 5000);  // 5 s covers powerSearch + settle
    setNow(Date.now());
    try {
      await station.atr("stop").catch(() => undefined);
      await station.returnToStart().catch(() => undefined);
      const after = await station.powerSearch();
      if (after?.atr_locked) {
        // Extend warm-up 3 s from ACTUAL lock time so the ATR settle
        // jitter after re-acquisition can't fake a still→moving→still
        // sequence that latches hasMoved.
        setRelockUntil(Date.now() + 3000);
        setNow(Date.now());
        toast.success(`Prism re-locked at home — walk to ${CORNER_NAMES[nextIdx]}`);
        speak(`Prism re-locked. Walk to corner ${CORNER_NAMES[nextIdx]}`);
      } else {
        toast.error(after?.last_error || "No prism at home — aim manually and retry");
      }
    } catch (e) {
      toast.error(e?.message || "Search failed");
    } finally {
      setHomeSearching(false);
    }
  };

  // ---- UI -------------------------------------------------------------
  const banner = (() => {
    if (phase === "home") {
      return homeSearching
        ? { tone: "search", title: "Home", subtitle: "SEARCHING…", cta: "Wait for lock" }
        : { tone: "default", title: "Step 1 · Home",
            subtitle: "Aim at your Home mark",
            cta: "Then tap SET HOME to search for the prism" };
    }
    if (phase === "complete") {
      return { tone: "captured", title: "Complete",
        subtitle: "ALL 4 CORNERS", cta: "Tap VIEW RESULTS · or tap a corner tile to re-shoot" };
    }
    // corner phase
    if (warming) {
      return { tone: "search", title: `Corner ${CORNER_NAMES[nextIdx]}`,
        subtitle: "LOCKING ON…", cta: "Hold the prism steady — ATR is settling" };
    }
    if (recovering || lockLost) {
      const stage = scope.search_stage;
      const subtitle =
        stage === "stage_2_home" ? "SEARCHING AT HOME" :
        stage === "stage_1_stare" ? "LOOKING NEARBY" :
        "PRISM LOST";
      const cta =
        stage === "stage_2_home"
          ? "Returned to home mark — running PowerSearch"
          : stage === "stage_1_stare"
          ? "Staring at last-known position…"
          : "Searching for the prism…";
      return { tone: "lost", title: `Corner ${CORNER_NAMES[nextIdx]}`,
        subtitle, cta };
    }
    if (busy) {
      return { tone: "search", title: `Corner ${CORNER_NAMES[nextIdx]}`,
        subtitle: "RECORDING…", cta: "Hold still" };
    }
    if (!hasMoved) {
      // Prism still parked on the mark it was locked at (usually home,
      // or the previous corner). Nudge the surveyor to start walking so
      // we don't fire a spurious auto-shot on the previous location.
      return { tone: "default", title: `Corner ${CORNER_NAMES[nextIdx]}`,
        subtitle: `Walk to ${CORNER_NAMES[nextIdx]}`,
        cta: "Pick up the pole — auto-record arms once you start moving" };
    }
    if (isStill && armed) {
      return { tone: "captured", title: `Corner ${CORNER_NAMES[nextIdx]}`,
        subtitle: "STILL — CAPTURING", cta: "" };
    }
    return { tone: "default", title: `Corner ${CORNER_NAMES[nextIdx]}`,
      subtitle: `Walk to ${CORNER_NAMES[nextIdx]}`,
      cta: "Stop on the mark — auto-records after ~0.6 s still" };
  })();

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-[#0A0B0E] text-white" data-testid="diag-guided">
      {/* FabAxis logo — tap to exit the guided run and return to the
          toolbox, mirroring the top-bar behaviour on the rest of the
          app. Keeps the branded field-controller feel and gives the
          surveyor a consistent way home from every screen. */}
      <button
        type="button"
        data-testid="diag-guided-logo-home"
        onClick={onExit}
        className="flex flex-none items-center gap-2 border-b border-[#2A2E39] bg-[#0A0B0E]/80 px-3 py-1.5 text-left backdrop-blur-sm hover:bg-[#14161A]"
        title="Return to toolbox"
      >
        <img src="/fabaxis-mark-light.png" alt="FabAxis" className="h-7 w-auto" />
        <div className="text-[11px] font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          <span className="text-[#B0000A]">Fab</span>Axis
        </div>
      </button>
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-2 px-3 py-2">
        <div className="flex-none"><StageBanner {...banner} /></div>

        {/* Corner progress dots — tap a captured corner to clear it so
            it becomes the next shot in line (for redoing a bad reading). */}
        <div className="grid flex-none grid-cols-4 gap-2">
          {CORNER_NAMES.map((name, i) => {
            const shot = (current.shots || []).find((s) => s.index === i);
            const done = !!shot;
            const isNext = phase === "corner" && i === nextIdx;
            const handleTap = async () => {
              if (!done || busy) return;
              if (!window.confirm(`Re-shoot corner ${name}?`)) return;
              try {
                const s = await diagApi.delShot(current.id, shot.id);
                setCurrent(s);
                toast.success(`Corner ${name} cleared — walk to ${name} again`);
              } catch { toast.error("Could not clear shot"); }
            };
            return (
              <button
                key={name}
                data-testid={`diag-guided-slot-${name}`}
                onClick={handleTap}
                disabled={!done}
                className={`rounded-md border py-2 text-center transition-all disabled:cursor-default ${
                  done ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F] hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
                    : isNext ? "animate-pulse border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]"
                    : "border-[#2A2E39] bg-[#0A0B0E] text-[#5A6065]"
                }`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
                title={done ? "Tap to re-shoot" : ""}
              >
                <div className="text-[9px] uppercase tracking-widest">Corner</div>
                <div className="text-2xl font-black leading-none">{name}</div>
                <div className="text-[9px] uppercase tracking-widest">
                  {done ? "tap to redo" : isNext ? "next" : "—"}
                </div>
              </button>
            );
          })}
        </div>

        {/* Phase-specific fills the remaining space */}
        {phase === "home" && (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Session settings card — one-row summary + controls */}
            <div className="flex flex-none items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2">
              <div className="min-w-0" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">
                  Session
                </div>
                <div className="truncate text-sm font-bold text-white">{current.name}</div>
              </div>
              <div style={{ fontFamily: "JetBrains Mono, monospace" }}
                   className="text-center">
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]"
                     style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                  Tolerance
                </div>
                <div className="text-sm font-bold text-white">
                  ±{current.tolerance_mm?.toFixed?.(1) ?? current.tolerance_mm} mm
                </div>
              </div>
              <div className="flex flex-none items-center gap-2">
                <button
                  data-testid="diag-guided-mute" onClick={() => setMuted((m) => !m)}
                  className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-2 text-[#8B939A] hover:border-[#009E8E] hover:text-white"
                  title={muted ? "Sound off" : "Sound on"}
                >
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
                <button
                  data-testid="diag-guided-exit" onClick={onExit}
                  className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
                  style={{ fontFamily: "Chakra Petch, sans-serif" }}
                >
                  Exit
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              <AimPad tool="diagonal" sessionId={current.id} compact />
            </div>
            <button
              data-testid="diag-guided-set-home" onClick={setHomeAndSearch}
              disabled={homeSearching}
              className="flex-none rounded-md border-2 border-[#009E8E] bg-gradient-to-br from-[#009E8E]/20 to-[#005a52]/20 py-4 text-lg font-black uppercase tracking-widest text-[#00E1C7] shadow-[inset_0_0_30px_rgba(0,158,142,0.2)] hover:from-[#009E8E]/30 disabled:opacity-40"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <div className="flex items-center justify-center gap-2">
                <HomeIcon size={20} strokeWidth={2.5} />
                {homeSearching ? "Searching…" : "Set Home & Lock Prism"}
              </div>
            </button>
          </div>
        )}

        {phase === "corner" && (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Live scope readout */}
            <div className="flex-none rounded-md border border-[#2A2E39] bg-[#14161A] p-2 text-center">
              <div className="flex items-center justify-center gap-4 text-[13px]"
                   style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <span className={scope.atr_locked ? "text-[#8DC63F]" : "text-[#FF3B30]"}>
                  <Zap size={14} className="inline" /> {scope.atr_locked ? "LOCKED" : "NO LOCK"}
                </span>
                <span className="text-[#8B939A]">
                  Hz {Number.isFinite(scope.hz) ? (scope.hz * 180 / Math.PI).toFixed(2) : "—"}°
                </span>
                <span className="text-[#8B939A]">
                  V {Number.isFinite(scope.v) ? (scope.v * 180 / Math.PI).toFixed(2) : "—"}°
                </span>
              </div>
            </div>
            {/* Big search-at-home fallback fills leftover space */}
            <button
              data-testid="diag-guided-search-home" onClick={searchAtHome}
              disabled={homeSearching}
              className="min-h-0 flex-1 rounded-md border border-[#F5A623]/50 bg-[#F5A623]/10 text-lg font-black uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/20 disabled:opacity-40"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <div className="flex items-center justify-center gap-2">
                <SearchIcon size={18} strokeWidth={2.5} />
                {homeSearching ? "Searching at Home…" : "Search at Home"}
              </div>
            </button>
            {/* Bottom bar with exit + mute so surveyor can bail mid-run */}
            <div className="flex flex-none items-center justify-end gap-2">
              <button
                data-testid="diag-guided-mute" onClick={() => setMuted((m) => !m)}
                className="rounded-md border border-[#2A2E39] bg-[#14161A] p-2 text-[#8B939A] hover:border-[#009E8E] hover:text-white"
                title={muted ? "Sound off" : "Sound on"}
              >
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
              <button
                data-testid="diag-guided-exit" onClick={onExit}
                className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                Exit
              </button>
            </div>
          </div>
        )}

        {phase === "complete" && (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {current.delta_mm != null && (
              <div className="flex-none rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3 text-center"
                   style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <div className="text-[10px] uppercase tracking-widest text-[#8B939A]"
                     style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                  Δ diagonal
                </div>
                <div className={`mt-1 text-4xl font-black ${current.within_tol ? "text-[#8DC63F]" : "text-[#FF3B30]"}`}>
                  {current.delta_mm.toFixed(1)} mm
                </div>
                <div className={`text-[11px] uppercase tracking-widest ${current.within_tol ? "text-[#8DC63F]" : "text-[#FF3B30]"}`}>
                  {current.within_tol ? "Pass" : "Fail"}
                </div>
              </div>
            )}
            {/* Post-completion prompt — one clear next step and the
                surveyor can already tap any green corner tile above to
                re-shoot. Green primary = view results, muted secondary
                hint below reminds them the tiles are tap-to-redo. */}
            <button
              data-testid="diag-guided-view-results" onClick={onExit}
              className="min-h-0 flex-1 rounded-md border-2 border-[#8DC63F] bg-gradient-to-br from-[#8DC63F]/20 to-[#4a6f1a]/20 text-xl font-black uppercase tracking-widest text-[#8DC63F] hover:from-[#8DC63F]/30"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <div className="flex items-center justify-center gap-2">
                <Crosshair size={22} strokeWidth={2.5} />
                View results
              </div>
            </button>
            <div className="flex-none rounded-md border border-dashed border-[#2A2E39] bg-[#0F1116] px-3 py-2 text-center text-[10px] uppercase tracking-widest text-[#8B939A]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Need to redo one? Tap any green corner tile above to re-shoot it.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
